"use client";

import React, { useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { Pagination } from "@heroui/pagination";

import { EmptyState } from "@/components/needs/EmptyState";
import { NeedCard } from "@/components/needs/NeedCard";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";
import { OrganizationTerminology } from "@/lib/services/terminologyService";
import { NeedWithArea } from "@/lib/types/prismaTypes";
import { getUrgencyOrder } from "@/lib/urgency-utils";

const ITEMS_PER_PAGE = 9;

type NeedComparator = (a: NeedWithArea, b: NeedWithArea) => number;

const NEED_COMPARATORS: Record<string, NeedComparator> = {
  "date-asc": (a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  "date-desc": (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  "due-asc": (a, b) =>
    (a.dueDate?.getTime() ?? Infinity) - (b.dueDate?.getTime() ?? Infinity),
  "due-desc": (a, b) =>
    (b.dueDate?.getTime() ?? 0) - (a.dueDate?.getTime() ?? 0),
  "items-asc": (a, b) => (a.targetQuantity ?? 0) - (b.targetQuantity ?? 0),
  "items-desc": (a, b) => (b.targetQuantity ?? 0) - (a.targetQuantity ?? 0),
  "urgency-asc": (a, b) =>
    getUrgencyOrder(b.urgency) - getUrgencyOrder(a.urgency),
  "urgency-desc": (a, b) =>
    getUrgencyOrder(a.urgency) - getUrgencyOrder(b.urgency),
};

function sortNeeds(needs: NeedWithArea[], sortBy: string): NeedWithArea[] {
  const comparator = NEED_COMPARATORS[sortBy];

  return comparator ? [...needs].sort(comparator) : [...needs];
}

const FILTER_PARAM_KEYS = ["q", "areas", "urgency", "category"] as const;

function hasActiveSearchParams(
  searchParams: ReturnType<typeof useSearchParams>,
): boolean {
  return FILTER_PARAM_KEYS.some((key) => Boolean(searchParams?.get(key)));
}

type NeedWithClaimStats = NeedWithArea & {
  claimStats?: { totalRequested: number; claimed: number; remaining: number };
};

function renderNeedsGrid(
  paginatedNeeds: NeedWithClaimStats[],
  isNoDataScenario: boolean,
  orgSlug: string,
  organizationName: string | undefined,
  terminology: OrganizationTerminology,
  variant: "active" | "completed",
  handleCategoryClick: (id: string) => void,
) {
  if (paginatedNeeds.length === 0) {
    return (
      <EmptyState
        actionAriaLabel={
          isNoDataScenario ? "Browse Other Organizations" : "Clear Filters"
        }
        actionHref={isNoDataScenario ? "/" : routes.org.needs(orgSlug)}
        ariaLabel={
          isNoDataScenario
            ? "No active needs right now"
            : "No needs available at the moment"
        }
        organizationName={organizationName}
        terminology={terminology}
        variant={isNoDataScenario ? "no-data" : "filtered"}
      />
    );
  }

  return paginatedNeeds.map((need) => (
    <div key={need.id} role="listitem">
      <NeedCard
        claimStats={need.claimStats}
        need={need}
        orgSlug={orgSlug}
        terminology={terminology}
        variant={variant}
        onCategoryClick={handleCategoryClick}
      />
    </div>
  ));
}

function getNeedsListVariantInfo(
  variant: "active" | "completed",
  terminology: OrganizationTerminology,
) {
  const isCompleted = variant === "completed";

  return {
    completedLabel: isCompleted ? "completed " : "",
    isCompleted,
    termLabel: isCompleted
      ? terminology.termCompleted
      : terminology.termAvailable,
    variantLabel: isCompleted ? "completed" : "available",
  };
}

function isNoDataState(
  totalNeedsCount: number | undefined,
  hasActiveFilters: boolean,
): boolean {
  return totalNeedsCount === 0 && !hasActiveFilters;
}

function buildCategoryClickParams(
  searchParams: ReturnType<typeof useSearchParams>,
  selectedCategory: string,
  categoryId: string,
): URLSearchParams {
  const params = new URLSearchParams(searchParams?.toString() ?? "");

  if (selectedCategory === categoryId) {
    params.delete("category");
  } else {
    params.set("category", categoryId);
  }

  return params;
}

function buildStatusText(
  count: number,
  completedLabel: string,
  currentPage: number,
  totalPages: number,
): string {
  if (count > 0) {
    return `Showing ${count} ${completedLabel}needs, page ${currentPage} of ${totalPages}`;
  }

  return "No needs available matching your current search - try adjusting filters or check back later";
}

function announcePageChange(page: number, totalPages: number) {
  const statusEl = document.createElement("div");

  statusEl.setAttribute("aria-live", "assertive");
  statusEl.className = "sr-only";
  statusEl.textContent = `Page ${page} of ${totalPages}, loading needs`;
  document.body.appendChild(statusEl);
  setTimeout(() => document.body.removeChild(statusEl), 1000);
}

/**
 * List component that displays a grid of need cards with filtering and pagination
 * Features enhanced accessibility including:
 * - Proper ARIA labeling
 * - Landmark roles
 * - Focus management
 * - Screen reader announcements
 * - Keyboard navigation
 */
export function NeedsList({
  needs,
  orgSlug,
  organizationName,
  terminology,
  totalNeedsCount,
  variant,
}: {
  needs: NeedWithClaimStats[];
  orgSlug: string;
  organizationName?: string;
  terminology: OrganizationTerminology;
  totalNeedsCount?: number;
  variant?: "active" | "completed";
}) {
  const resolvedVariant = variant ?? "active";
  const router = useRouter();
  const rawSearchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const searchParams = rawSearchParams ?? new URLSearchParams();
  const sortBy = searchParams.get("sort") ?? "urgency-desc";
  const selectedCategory = searchParams.get("category") ?? "";

  const filteredNeeds = needs.filter((need) => need.status !== "expired");
  const sortedNeeds = sortNeeds(filteredNeeds, sortBy);

  // Pagination
  const totalPages = Math.ceil(sortedNeeds.length / ITEMS_PER_PAGE);

  const paginatedNeeds = sortedNeeds.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const hasActiveFilters = hasActiveSearchParams(rawSearchParams);
  const isNoDataScenario = isNoDataState(totalNeedsCount, hasActiveFilters);

  const handleCategoryClick = (categoryId: string) => {
    const params = buildCategoryClickParams(
      rawSearchParams,
      selectedCategory,
      categoryId,
    );
    const isDeselecting = selectedCategory === categoryId;

    void EventPublisher.publishEvent(EventKeys.ORG_NEED_FILTER_APPLY, {
      current_page: currentPage,
      key: "category",
      organization_id: orgSlug,
      total_needs: needs.length,
      value: isDeselecting ? null : categoryId,
    });
    router.push(`/${orgSlug}/needs?${params.toString()}`);
  };

  // Handle pagination change
  const handlePageChange = (page: number) => {
    void EventPublisher.publishEvent(EventKeys.ORG_NEED_PAGINATION_CHANGE, {
      from_page: currentPage,
      needs_per_page: ITEMS_PER_PAGE,
      organization_slug: orgSlug,
      to_page: page,
      total_needs: needs.length,
      total_pages: totalPages,
    });

    setCurrentPage(page);
    announcePageChange(page, totalPages);
  };

  const needsListId = "needs-list";
  const paginationId = "needs-pagination";
  const paginationAriaLabel = `Page ${currentPage} of ${totalPages}`;
  const { completedLabel, termLabel, variantLabel } = getNeedsListVariantInfo(
    resolvedVariant,
    terminology,
  );
  const statusText = buildStatusText(
    paginatedNeeds.length,
    completedLabel,
    currentPage,
    totalPages,
  );

  return (
    <section aria-labelledby={needsListId} className="space-y-6" role="region">
      <h2 className="sr-only" id={needsListId}>
        List of {variantLabel} needs
      </h2>

      <div aria-live="polite" className="sr-only" role="status">
        {statusText}
      </div>

      {/* Needs Grid */}
      <div
        aria-label={`${termLabel} ${terminology.need.plural.toLowerCase()} cards`}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        role="list"
      >
        {renderNeedsGrid(
          paginatedNeeds,
          isNoDataScenario,
          orgSlug,
          organizationName,
          terminology,
          resolvedVariant,
          handleCategoryClick,
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          aria-label="Pagination navigation"
          className="flex justify-center mt-8 bg-default-50 p-4 rounded-lg"
          id={paginationId}
        >
          <Pagination
            disableCursorAnimation
            loop
            showControls
            aria-controls={needsListId}
            aria-label={paginationAriaLabel}
            color="primary"
            page={currentPage}
            size="md"
            total={totalPages}
            variant="flat"
            onChange={handlePageChange}
          />
        </nav>
      )}
    </section>
  );
}
